import { useState, useEffect, useRef } from "react";
import RightTimeApi from "@/API";
import Enums from "@/enums";
import RecentsTable from "@elements/tables/RecentsTable";
import ReportViewModal from "@modals/ReportViewModal";
import ReportSelectModal from "@modals/ReportSelectModal";
import { toast } from "react-toastify";
import { downloadResponseAsFile } from "@/utils/authenticatedFileDownload";


export default function CompanyRecentsTab(props: any) {
  type ReportOption = {
    reportDefinitionId: number;
    name: string;
  };

  const [isAdmin, UpdateAdmin] = useState(false);
  const [siteImageURL, updateSiteImageURL] = useState("");
  const [companyImageURLs, updateCompanyImageURLs] = useState<string[]>([]);
  const [simRoleId, updateSimRoleId] = useState();
  const [instructions, updateInstructions] = useState("");
  const [isReportSelectOpen, setIsReportSelectOpen] = useState(false);
  const [activeReportTestInstanceId, setActiveReportTestInstanceId] = useState<number | null>(null);
  const [availableReports, setAvailableReports] = useState<ReportOption[]>([]);
  const [activeReportDefinitionId, setActiveReportDefinitionId] = useState<number | null>(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportModalTitle, setReportModalTitle] = useState("Report");
  const [reportModalHtml, setReportModalHtml] = useState("");

  const isOnThisTab = Boolean(props?.tabData?.activeTab === "recents");
  useEffect(() => {
    UpdateAdmin(props.tabData?.adminPerms.find(p => p.value === Enums.ROLE_PERMS.SystemAdmin) !== undefined);
  }, []);

  useEffect(() => {
    if (props?.tabData?.company) {
      const company = props.tabData.company;
      const primaryImageId =
        company.imageId > 0 ? Number(company.imageId) : null;
      const imageIds: number[] = [];

      if (Array.isArray(company.images) && company.images.length > 0) {
        const otherImageIds = company.images
          .filter((id: number) => id > 0)
          .map((id: number) => Number(id))
          .filter((id: number) => id !== primaryImageId);

        if (primaryImageId) {
          imageIds.push(primaryImageId, ...otherImageIds);
        } else {
          imageIds.push(...otherImageIds);
        }
      } else if (primaryImageId) {
        imageIds.push(primaryImageId);
      }

      updateCompanyImageURLs(
        imageIds.map((id) => RightTimeApi.URL("/Image/" + id))
      );
    } else {
      updateCompanyImageURLs([]);
    }
  }, [props?.tabData?.company, simRoleId]);

  // Early return checks after all hooks are declared (Rules of Hooks compliance)
  if (!props || !props.tabData) {
    return null;
  }



  if (!isOnThisTab) return null;

  async function renderAndOpenReport(testInstanceId: number, selectedReport: ReportOption) {
    props.tabData?.updateSpinner?.(true);
    try {
      const renderResp = await RightTimeApi.postAsync(
        `/TestInstance/${testInstanceId}/Report/${selectedReport.reportDefinitionId}/Render`,
        {}
      );

      if (!renderResp?.ok || !renderResp?.jsonData) {
        toast.error("Unable to render the selected report.");
        return;
      }

      setActiveReportTestInstanceId(testInstanceId);
      setActiveReportDefinitionId(selectedReport.reportDefinitionId);
      setReportModalTitle(renderResp.jsonData.reportName || selectedReport.name || "Report");
      setReportModalHtml(renderResp.jsonData.html || "");
      setIsReportModalOpen(true);
    } finally {
      props.tabData?.updateSpinner?.(false);
    }
  }

  async function onReportClick(testInstanceId: number) {
    if (!testInstanceId) return;

    props.tabData?.updateSpinner?.(true);
    let reportsResp;
    try {
      reportsResp = await RightTimeApi.getAsync(`/TestInstance/${testInstanceId}/Reports`);
    } finally {
      props.tabData?.updateSpinner?.(false);
    }

    if (!reportsResp?.ok || !reportsResp?.jsonData) {
      toast.error("Unable to load reports for this test.");
      return;
    }

    const reports = reportsResp.jsonData.reports || [];
    if (!Array.isArray(reports) || reports.length === 0) {
      toast.info("No reports are available for this test.");
      return;
    }

    if (reports.length === 1) {
      await renderAndOpenReport(testInstanceId, reports[0]);
      return;
    }

    setActiveReportTestInstanceId(testInstanceId);
    setAvailableReports(reports);
    setIsReportSelectOpen(true);
  }

  async function onSelectReport(selectedReport: ReportOption) {
    if (!activeReportTestInstanceId) {
      toast.error("Unable to determine which test report to render.");
      return;
    }

    setIsReportSelectOpen(false);
    await renderAndOpenReport(activeReportTestInstanceId, selectedReport);
  }

  async function onDownloadPdf() {
    if (!activeReportTestInstanceId || !activeReportDefinitionId) {
      toast.error("Unable to determine report context for PDF download.");
      return;
    }

    props.tabData?.updateSpinner?.(true);
    try {
      const response = await RightTimeApi.getAsync(
        `/TestInstance/${activeReportTestInstanceId}/Report/${activeReportDefinitionId}/Pdf`
      );

      if (!response?.ok) {
        toast.error("Unable to generate report PDF.");
        return;
      }

      const ok = await downloadResponseAsFile(
        response,
        `report_${activeReportDefinitionId}_${activeReportTestInstanceId}.pdf`
      );
      if (!ok) toast.error("Unable to generate report PDF.");
      else toast.success("Report PDF downloaded.");
    } finally {
      props.tabData?.updateSpinner?.(false);
    }
  }

  return (
    <>
      <div className="pageContent recentsPage">
        {companyImageURLs.length > 0 && (
          <div className="header">
            <div className="headerColumn companyLogos">
              {companyImageURLs.map((url) => (
                <img key={url} src={url} className="logo company" alt="" />
              ))}
            </div>

            {siteImageURL ? (
              <div className="headerColumn">
                <img src={siteImageURL} className="logo site" alt="" />
              </div>
            ) : null}
          </div>
        )}

        {instructions && (
          <div className="instructions">
            {instructions}
          </div>
        )}

        <RecentsTable tabData={props.tabData} onReportClick={onReportClick} limitToUserSites />

        <ReportSelectModal
          isOpen={isReportSelectOpen}
          reports={availableReports}
          onClose={() => setIsReportSelectOpen(false)}
          onSelect={onSelectReport}
        />

        <ReportViewModal
          isOpen={isReportModalOpen}
          title={reportModalTitle}
          html={reportModalHtml}
          onDownloadPdf={onDownloadPdf}
          onClose={() => setIsReportModalOpen(false)}
        />

      </div>
    </>
  );
};

